import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {Evaluator, Version} from "@cm/graph-v3"
import {versionChain} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import type {ColorValue, ImageLikeNode, LightType, PositionValue} from "#template/nodes/types"
import {colorValue, lightType, positionValue} from "#template/nodes/validation"
import {imageLike} from "#template/nodes/image/fundamental"
import {TargetableObjectNode} from "#template/nodes/object/targetable-object-node"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import type {OnCompileContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {objectNodeParameters} from "#template/nodes/object/validation"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {areaLightRadiance} from "#template/utils/light-intensity"
import {visitNone} from "#template/template-node/visit-helpers"

export type AreaLightParameters = {
    name: string
    intensity: number
    width: number
    height: number
    color: ColorValue
    texture?: ImageLikeNode
    target: PositionValue
    targeted: boolean
    on: boolean
    directionality: number
    visibleDirectly: boolean
    visibleInReflections: boolean
    visibleInRefractions: boolean
    transparent: boolean
    lightType: LightType
} & ObjectNodeParameters

type V0 = {
    name: string
    intensity: number
    width: number
    height: number
    color: [number, number, number]
    target: [number, number, number]
    on: boolean
    directionality: number
    visibleDirectly: boolean
    visibleInReflections: boolean
    visibleInRefractions: boolean
    transparent: boolean
} & ObjectNodeParameters

type V1 = V0 & {lightType: LightType}
type V2 = V1 & {targeted: boolean}

const v0: Version<V0, V1> = {
    toNextVersion: (parameters) => {
        return {...parameters, lightType: "Corona"}
    },
}

const v1: Version<V1, V2> = {
    toNextVersion: (parameters) => {
        return {...parameters, targeted: true}
    },
}

const areaLightDefinition: ObjectNodeDefinition<AreaLightParameters> = {
    nodeClass: "AreaLight",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                intensity: z.number(),
                width: z.number(),
                height: z.number(),
                color: colorValue,
                texture: z.optional(imageLike),
                target: positionValue,
                targeted: z.boolean(),
                on: z.boolean(),
                directionality: z.number(),
                visibleDirectly: z.boolean(),
                visibleInReflections: z.boolean(),
                visibleInRefractions: z.boolean(),
                transparent: z.boolean(),
                lightType: lightType,
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([v0, v1]),
}

@registerTemplateGraphNode
export class AreaLight extends TargetableObjectNode<AreaLightParameters> {
    public override get definition() {
        return areaLightDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<AreaLightParameters> {
        const {evaluator} = context
        const {
            width,
            height,
            color,
            targeted,
            on,
            intensity,
            directionality,
            visibleDirectly,
            visibleInReflections,
            visibleInRefractions,
            transparent,
            lightType,
        } = this.parameters

        const scope = evaluator.getScope(this)

        const mappedIntensity = lightType === "Corona" ? intensity : areaLightRadiance(intensity, width, height)

        const texture = scope.pureLambda(
            evaluator.evaluate(this.parameters.texture ?? null),
            (texture) => {
                if (!texture) return undefined
                return texture
            },
            "displacementImageNode",
        )

        this.setupObject(scope, context, "AreaLight", undefined, undefined, ({transform: objectTransform, isInteracting, ...objectProps}) => {
            const transform = this.targetedTransform(scope, objectTransform, isInteracting)

            return {
                preDisplayList: scope.struct<SceneNodes.AreaLight>("AreaLight", {
                    type: "AreaLight",
                    ...objectProps,
                    transform,
                    width,
                    height,
                    color,
                    texture,
                    on,
                    intensity: mappedIntensity,
                    directionality,
                    visibleDirectly,
                    visibleInReflections,
                    visibleInRefractions,
                    target: this.setupTargetLiveValue(scope, context),
                    targeted,
                    transparent: transparent,
                }),
            }
        })

        return visitNone(this.parameters)
    }
}
